import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

const MembershipPlans = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const plans = [
    {
      name: "Free",
      subtitle: "Basic exploration tier",
      price: "₹ 0",
      period: "mos",
      popular: false,
      features: [
        { text: "Create & Edit Profile", included: true },
        { text: "View Free Member Profiles", included: true },
        { text: "View Direct Mobile Numbers", included: false },
        { text: "Horoscope Porutham Report", included: false }
      ],
      buttonVariant: "outline"
    },
    {
      name: "Gold",
      subtitle: "Comprehensive matchmaking plan",
      price: "₹ 3,499",
      period: "6 mos",
      popular: true,
      features: [
        { text: "Unlimited Express Interests", included: true },
        { text: "Access 50 Verified Phone Numbers", included: true },
        { text: "SecureConnect Call Masking", included: true },
        { text: "Full Horoscope Compatibility", included: true }
      ],
      buttonVariant: "primary"
    },
    {
      name: "Platinum",
      subtitle: "Dedicated advisor assistance",
      price: "₹ 7,499",
      period: "6 mos",
      popular: false,
      features: [
        { text: "Dedicated Relationship Manager", included: true },
        { text: "Unlimited Verified Number Unlocks", included: true },
        { text: "Priority Search Placement", included: true },
        { text: "100% Privacy Masking", included: true }
      ],
      buttonVariant: "outline"
    }
  ];

  return (
    <section id="plans" className="relative w-full overflow-hidden scroll-mt-16">
      {/* Top Gradient Background Band with Pattern */}
      <div
        className="w-full bg-gradient-to-r from-[#7c1d6f] via-[#d63d59] to-[#f03f16] pt-16 sm:pt-20 md:pt-24 pb-48 sm:pb-56 md:pb-60 px-4 sm:px-6 relative text-center"
        style={{
          backgroundImage: "url('/images/img_frame_2147239407.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-[1360px] mx-auto relative z-10">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-white/90 mb-3">
            PRICING
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-[48px] font-bold text-white mb-4 tracking-tight">
            Pick Your Plan &amp; Meet Your Match
          </h2>
          <p className="text-base sm:text-lg text-white/80 max-w-xl mx-auto mb-6">
            Flexible membership plans designed to help you connect with genuine, verified matches.
          </p>
          <div className="inline-block bg-[#8c2250]/70 backdrop-blur-md border border-white/30 text-white font-bold text-xs uppercase px-6 py-2 rounded-full tracking-widest shadow-md mb-2">
            NO HIDDEN RENEWAL FEES
          </div>
        </div>
      </div>

      {/* Bottom White Background Band with Overlapping Cards */}
      <div className="w-full bg-white relative pt-4 pb-20 sm:pb-28">
        <div className="w-full max-w-[1240px] mx-auto px-4 sm:px-6 -mt-40 sm:-mt-44 md:-mt-48 relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-end">
            {plans.map((plan, index) => (
              <PlanCard key={index} plan={plan} navigate={navigate} isAuthenticated={isAuthenticated} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const PlanCard = ({ plan, navigate, isAuthenticated }) => (
  <div
    className={`bg-white rounded-[28px] sm:rounded-[32px] p-6 sm:p-8 flex flex-col justify-between text-left transition-all ${plan.popular
      ? 'shadow-2xl border-2 border-red-200 relative z-20 md:-translate-y-10 lg:-translate-y-12 scale-100 md:scale-[1.03] min-h-[390px] sm:min-h-[420px]'
      : 'shadow-lg border border-gray-100 relative z-10 md:translate-y-2 min-h-[350px] sm:min-h-[380px]'
      }`}
  >
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-3xl sm:text-4xl font-semibold text-gray-900">
          {plan.name}
        </h3>
        {plan.popular && (
          <div className="inline-flex items-center gap-1.5 bg-[#fff0f3] border border-[#f03f16] text-[#f03f16] rounded-full px-3.5 py-1 text-xs font-bold shadow-sm">
            <span className="text-xs">⭐</span>
            <span>Popular</span>
          </div>
        )}
      </div>

      <p className="text-xs sm:text-sm text-gray-500 mb-4">{plan.subtitle}</p>
      <div className="border-b border-gray-100 mb-6"></div>

      {/* Price */}
      <div className="mb-6">
        <span className="text-3xl sm:text-4xl font-semibold text-gray-900">{plan.price}</span>
        <span className="text-gray-400 font-medium text-base sm:text-lg"> / {plan.period}</span>
      </div>

      {/* Features List */}
      <div className="space-y-3.5 mb-8">
        {plan.features.map((feature, index) => (
          <div key={index} className="flex items-center gap-3">
            {feature.included ? (
              <svg className="w-5 h-5 text-gray-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-gray-300 flex-shrink-0 ml-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className={`text-sm sm:text-base font-medium ${feature.included ? 'text-gray-900' : 'text-gray-400'}`}>
              {feature.text}
            </span>
          </div>
        ))}
      </div>
    </div>

    {/* Upgrade Button */}
    {plan.buttonVariant === 'primary' ? (
      <button
        onClick={() => navigate(isAuthenticated ? '/plans' : '/signup')}
        className="w-full bg-gradient-to-r from-[#8a216b] to-[#f03f16] text-white py-2.5 px-6 rounded-full font-bold text-base sm:text-lg flex items-center justify-center gap-2 shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
      >
        <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
        <span>Upgrade Now</span>
      </button>
    ) : (
      <button
        onClick={() => navigate(isAuthenticated ? '/plans' : '/signup')}
        className="group w-full border-2 hover:border-transparent hover:bg-gradient-to-r hover:from-[#8a216b] hover:to-[#f03f16] py-2.5 px-6 rounded-full font-bold text-base sm:text-lg flex items-center justify-center gap-2 shadow-sm transition-all cursor-pointer"
      >
        <svg className="w-6 h-6 flex-shrink-0 fill-[url(#upgradeHeartGradient)] group-hover:fill-white transition-colors" viewBox="0 0 24 24">
          <defs>
            <linearGradient id="upgradeHeartGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#8a216b" />
              <stop offset="100%" stopColor="#f03f16" />
            </linearGradient>
          </defs>
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
        <span className="bg-gradient-to-r from-[#8a216b] to-[#f03f16] bg-clip-text text-transparent group-hover:bg-none group-hover:text-white transition-colors">
          Upgrade Now
        </span>
      </button>
    )}
  </div>
);

export default MembershipPlans;